// ---------------------------------------------------------------------------
// The preparation ticket itself.
//
// The sibling of ReceiptSheet, and deliberately NOT a variant of it. There is
// no shared component, no shared config type and no shared props between the
// two. They are two documents for two people:
//
//   the receipt goes to the customer and carries money;
//   this goes above a cutting block and carries none.
//
// You will find no price, no total and no currency in this file. Not hidden
// behind a flag - absent. `KitchenConfig` has no money field to read, so there
// is no switch anybody could flip and no line anybody could uncomment. A price
// on a cutting instruction invites somebody to hand it to a customer as a bill.
//
// Everything else on the paper is decided by `config`, and a line switched off
// is not rendered at all rather than rendered invisibly, because a thermal
// printer prints what is in the DOM.
//
// Like the receipt, this is the SAME component the designer previews and the
// till prints, so what an owner arranges is what comes out.
//
// THE LANGUAGE ON THE PAPER IS THE DESIGN'S, NOT THE SCREEN'S.
//
// Every label below is read out of the dictionary the CONFIG names, not out of
// the `t` the interface happens to be using. That distinction is the whole
// point of the "printed language" setting: an owner working in English must be
// able to print an Arabic ticket for a butcher who reads Arabic. Reaching for
// `t` here - or borrowing a label from the orders screen or the receipt - is
// what left an Arabic ticket printing "Customer" and "Cashier".
// ---------------------------------------------------------------------------

import clsx from 'clsx';
import { YaghiLogo } from '../common/YaghiLogo';
import { useI18n } from '../../i18n/I18nProvider';
import { ar as arabic, en as english } from '../../i18n/translations';
import { formatDateTime, formatQuantity, unitLabel } from '../../utils/format';
import { contentWidthMm, type ReceiptWidth } from '../../utils/printPaper';
import { optionLine } from '../../utils/optionLabel';
import type { KitchenConfig } from '../../types/design';
import type { KitchenTicket } from '../../types';

interface Props {
  config: KitchenConfig;
  ticket: KitchenTicket;
  /// The designer sets this so its preview does not claim the id the print
  /// stylesheet targets.
  forPreview?: boolean;
}

const BODY_TEXT: Record<KitchenConfig['fontSize'], string> = {
  SMALL: 'text-[10px]',
  MEDIUM: 'text-xs',
  LARGE: 'text-sm',
};

/// The product name gets its own scale. It is what somebody reads from two
/// steps away with their hands full, and it wants to be bigger than the rest
/// of the ticket rather than the whole ticket being bigger.
const NAME_TEXT: Record<KitchenConfig['productNameSize'], string> = {
  SMALL: 'text-sm',
  MEDIUM: 'text-base',
  LARGE: 'text-lg',
};

const LOGO_HEIGHT: Record<KitchenConfig['logoSize'], number> = {
  SMALL: 28,
  MEDIUM: 40,
  LARGE: 56,
};

const ALIGN: Record<KitchenConfig['logoAlignment'], string> = {
  LEFT: 'justify-start',
  CENTER: 'justify-center',
  RIGHT: 'justify-end',
};

export function KitchenTicketSheet({ config, ticket, forPreview = false }: Props) {
  const { locale } = useI18n();

  const paperWidth = (config.widthMm === 58 ? 58 : 80) as ReceiptWidth;
  const previewMm = contentWidthMm(paperWidth);

  // Which language the PAPER is in. A butcher who reads Arabic gets Arabic
  // regardless of the language the till happens to be showing. BILINGUAL has
  // no single answer, so it follows the screen - which is the only reading
  // that never puts two labels where one belongs.
  const printLocale = config.language === 'AR' ? 'ar' : config.language === 'EN' ? 'en' : locale;

  /// The labels for the paper. `k` is the ticket's own block, so nothing here
  /// can be dragged out of step by a change on another screen.
  const k = (printLocale === 'ar' ? arabic : english).kitchen;

  const pick = (en: string, ar: string) =>
    config.language === 'AR' ? ar : config.language === 'EN' ? en : locale === 'ar' ? ar : en;

  /// A product name. In BILINGUAL both names are printed, one under the other,
  /// which is the only thing that makes bilingual different from picking a
  /// language - and on a cutting instruction it is genuinely useful, because
  /// whoever picks the ticket up reads one of the two.
  const productName = (nameEn: string, nameAr: string) =>
    config.language === 'BILINGUAL' && nameEn !== nameAr ? (
      <>
        <span className="block">{locale === 'ar' ? nameAr : nameEn}</span>
        <span className="block font-normal opacity-80">{locale === 'ar' ? nameEn : nameAr}</span>
      </>
    ) : (
      pick(nameEn, nameAr)
    );

  const divider = () => <Divider style={config.dividerStyle} />;

  const isDelivery = ticket.orderType.toUpperCase().includes('DELIVERY');

  /// WHICH BANNER, from the ticket's frozen snapshot - never re-derived from
  /// a fee or from today's cart. COUNTER (a legacy sale from before the
  /// choice existed) maps to none: nothing recorded how that order left the
  /// shop, and a banner would be a guess printed in large type.
  const fulfilmentKind =
    ticket.orderType === 'DELIVERY' || isDelivery
      ? ('DELIVERY' as const)
      : ticket.orderType === 'PICKUP'
        ? ('PICKUP' as const)
        : ticket.orderType === 'ON_TABLE'
          ? ('ON_TABLE' as const)
          : null;

  /// The big Latin word, deliberately the same in every language - exactly
  /// as the DELIVERY box has always been. A shape the whole shop recognises
  /// from across the room, whatever script the rest of the paper is in.
  const BANNER_LATIN: Record<'DELIVERY' | 'PICKUP' | 'ON_TABLE', string> = {
    DELIVERY: 'DELIVERY',
    PICKUP: 'PICKUP',
    ON_TABLE: 'ON TABLE',
  };
  /// The Arabic line beneath it, shown whenever the paper speaks Arabic.
  const BANNER_ARABIC: Record<'DELIVERY' | 'PICKUP' | 'ON_TABLE', string> = {
    DELIVERY: arabic.kitchen.fulfilmentDelivery,
    PICKUP: arabic.kitchen.fulfilmentPickup,
    ON_TABLE: arabic.kitchen.fulfilmentOnTable,
  };
  const bannerShowsArabic = printLocale === 'ar' || config.language === 'BILINGUAL';

  /// The order type as a word somebody reads, rather than as the enum the
  /// system stores. The boxed heading above is the one that stays "DELIVERY"
  /// in every language; this row is prose.
  const orderTypeWord = (() => {
    const key = `type${ticket.orderType.toUpperCase()}` as keyof typeof k;
    const word = k[key];
    // An order type this build has no word for prints as it is stored, which
    // is ugly but true, rather than as a blank.
    return typeof word === 'string' ? word : ticket.orderType;
  })();

  return (
    <div
      id={forPreview ? undefined : 'ticket-sheet'}
      className={clsx('ticket-sheet mx-auto bg-white p-4 text-charcoal', BODY_TEXT[config.fontSize])}
      style={{ width: `${previewMm}mm` }}
      dir={printLocale === 'ar' ? 'rtl' : 'ltr'}
    >
      {config.showLogo ? (
        <div className={clsx('mb-1.5 flex', ALIGN[config.logoAlignment])}>
          <YaghiLogo height={LOGO_HEIGHT[config.logoSize]} forPrint decorative />
        </div>
      ) : null}

      <p className="text-center text-sm font-bold uppercase tracking-wide">
        {k.forPreparation}
      </p>

      {/* Shouted across the shop, when the shop wants it shouted. The same
          box, border and type for all three ways an order can leave - the
          butcher must recognise PICKUP and ON TABLE at exactly the distance
          they already recognise DELIVERY. A table order adds its table in
          the largest type in the box: which table IS the instruction. */}
      {config.showDeliveryHeading && fulfilmentKind ? (
        <div className="mt-1 border-2 border-charcoal px-2 py-0.5 text-center">
          <p className="text-sm font-bold uppercase tracking-wide">
            {BANNER_LATIN[fulfilmentKind]}
          </p>
          {bannerShowsArabic ? (
            <p className="text-sm font-bold">{BANNER_ARABIC[fulfilmentKind]}</p>
          ) : null}
          {fulfilmentKind === 'ON_TABLE' && ticket.tableLabel ? (
            // The paper's own direction for the word; the label itself is an
            // isolated LTR run so «A4» can never come out as «4A» on an
            // Arabic ticket.
            <p className="text-xl font-bold tracking-wide">
              {bannerShowsArabic ? arabic.kitchen.tableWord : english.kitchen.tableWord}{' '}
              <span className="numeric" dir="ltr">
                {ticket.tableLabel}
              </span>
            </p>
          ) : null}
        </div>
      ) : null}
      {config.showUrgentHeading ? (
        <p className="mt-1 border-2 border-charcoal px-2 py-0.5 text-center text-sm font-bold uppercase tracking-wide">
          {k.urgentHeading}
        </p>
      ) : null}

      <p className="numeric mt-1 text-center text-lg font-bold" dir="ltr">
        {ticket.ticketNumber}
      </p>

      {/* Which sale or order it belongs to, so the butcher and the till can
          find each other. */}
      {config.showSaleNumber || config.showOrderNumber ? (
        <p className="numeric text-center text-xs" dir="ltr">
          {ticket.referenceNumber}
        </p>
      ) : null}

      {divider()}

      <div className="space-y-0.5">
        {config.showOrderType ? (
          <Line label={k.orderTypeLabel} value={orderTypeWord} />
        ) : null}

        {config.showCustomerName && ticket.customerName ? (
          <Line label={k.customerLabel} value={ticket.customerName} />
        ) : null}
        {/* An order from the menu: the number the customer holds, and - when
            the design asks for them - their phone and where it goes. */}
        {ticket.onlineOrderNumber ? (
          <Line label={k.onlineOrderLabel} value={ticket.onlineOrderNumber} numeric />
        ) : null}
        {config.showCustomerPhone && ticket.customerPhone ? (
          <Line label={k.phoneLabel} value={ticket.customerPhone} numeric />
        ) : null}
        {config.showDeliveryAddress && ticket.deliveryAddress ? (
          <Line label={k.addressLabel} value={ticket.deliveryAddress} />
        ) : null}

        {config.showRequestedTime && ticket.requestedFor ? (
          <Line
            label={k.wantedBy}
            value={formatDateTime(ticket.requestedFor, printLocale)}
            numeric
          />
        ) : null}

        {config.showCashierName ? (
          <Line label={k.cashierLabel} value={ticket.printedBy.fullName} />
        ) : null}

        {config.showDateTime ? (
          <Line
            label={k.printedAt}
            value={formatDateTime(ticket.createdAt, printLocale)}
            numeric
          />
        ) : null}
      </div>

      {divider()}

      {/* --- what to cut. No prices, because there are none to print. ---- */}
      <ul className="space-y-2">
        {ticket.items.map((item, index) => (
          <li key={item.id}>
            {/* The combo this line came out of, as a heading over its first
                component on THIS bench - so the butcher knows the crispy
                and the fries are one order's bundle. Routing was decided by
                each product's real category; this is a word, not a route. */}
            {item.comboGroupKey &&
            (index === 0 || ticket.items[index - 1]?.comboGroupKey !== item.comboGroupKey) ? (
              <p className={clsx('mt-1 font-bold uppercase tracking-wide', BODY_TEXT[config.fontSize])}>
                ★ {pick(item.comboNameEn ?? '', item.comboNameAr ?? '')}
              </p>
            ) : null}
            <div className="flex items-baseline justify-between gap-2">
              <span
                className={clsx(
                  'flex-1 break-words',
                  NAME_TEXT[config.productNameSize],
                  config.boldProductName && 'font-bold',
                )}
              >
                {productName(item.nameEn, item.nameAr)}
              </span>

              {config.showWeightOrQuantity ? (
                <span
                  className={clsx(
                    'numeric shrink-0',
                    NAME_TEXT[config.productNameSize],
                    config.boldQuantity && 'font-bold',
                  )}
                  dir="ltr"
                >
                  {formatQuantity(item.quantity)} {unitLabel(item.sellingUnit, printLocale)}
                </span>
              ) : null}
            </div>

            {/* WHICH TYPE TO PREPARE. On its own line, in the same weight
                as the product name, because the butcher reading this at arm's
                length has to see it - a ticket saying only "طاووق متبل" gets
                the wrong meat marinated. */}
            {optionLine(
              { ar: item.variantLabelAr, en: item.variantLabelEn },
              pick(item.variantNameEn ?? '', item.variantNameAr ?? ''),
              printLocale,
            ) ? (
              <p
                dir={printLocale === 'ar' ? 'rtl' : 'ltr'}
                className={clsx(
                  'break-words',
                  NAME_TEXT[config.productNameSize],
                  config.boldProductName && 'font-bold',
                )}
              >
                {/* The TICKET's own label, not a word chosen here. It used to
                    say "النوع" on every ticket, which was right for tawook and
                    wrong for the chips going out beside it. */}
                {optionLine(
                  { ar: item.variantLabelAr, en: item.variantLabelEn },
                  pick(item.variantNameEn ?? '', item.variantNameAr ?? ''),
                  printLocale,
                )}
              </p>
            ) : null}

            {config.showCategory ? (
              <p className="text-[10px] uppercase tracking-wide">
                {pick(item.categoryNameEn, item.categoryNameAr)}
              </p>
            ) : null}

            {config.showItemNotes && item.note ? <p className="break-words">↳ {item.note}</p> : null}
            {config.showCuttingInstructions && item.preparationNotes ? (
              <p className="break-words font-semibold">↳ {item.preparationNotes}</p>
            ) : null}
          </li>
        ))}
      </ul>

      {config.showOrderNotes && ticket.orderNotes ? (
        <>
          {divider()}
          <p className="break-words font-semibold">{ticket.orderNotes}</p>
        </>
      ) : null}

      {/* Room to write on. Real blank lines with a rule under each, because a
          butcher writing "done, 1.7kg" on the ticket is how a shop keeps track
          without a screen at the block. */}
      {config.handwritingSpaceLines > 0 ? (
        <div className="mt-3 space-y-4">
          {Array.from({ length: config.handwritingSpaceLines }, (_, index) => (
            <div key={index} className="border-b border-charcoal/30" />
          ))}
        </div>
      ) : null}

      {divider()}

      {/* Printed so a duplicate in the kitchen is obvious to whoever is
          holding it. Not configurable: a second copy nobody notices is a
          second cut of meat. */}
      {ticket.reprintCount > 0 ? (
        <p className="text-center font-bold">
          {k.copyNumber.replace('{number}', String(ticket.reprintCount + 1))}
        </p>
      ) : null}

      {/* Also not configurable, for the same reason the receipt marks a
          duplicate: this must never be mistakable for a bill. */}
      <p className="mt-1 text-center text-[10px] font-semibold">{k.notABill}</p>
    </div>
  );
}

function Divider({ style }: { style: KitchenConfig['dividerStyle'] }) {
  if (style === 'NONE') return <div className="my-2" />;

  return (
    <div
      className={clsx(
        'my-2 border-t border-charcoal/40',
        style === 'SOLID' ? 'border-solid' : style === 'DOTTED' ? 'border-dotted' : 'border-dashed',
      )}
    />
  );
}

function Line({ label, value, numeric }: { label: string; value: string; numeric?: boolean }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="shrink-0 font-semibold">{label}</span>
      <span className={clsx('min-w-0 break-words text-end', numeric && 'numeric')}>{value}</span>
    </div>
  );
}
